import { ErrorBanner } from '@core/components';
import type { DomainError } from '@core/domain';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Dialog, LoadingSpinner, TextInput } from '@soteria/design-system-ve';
import { useCallback, useEffect, useState } from 'react';

import * as styles from './EditCommunitySettingDialog.css';

const MAX_NAME_LENGTH = 200;

function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, c => c.toUpperCase());
}

interface EditCommunitySettingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  itemType: string;
  loading: boolean;
  errors: DomainError[] | null;
  resetErrors: () => void;
  onSave: (newName: string) => Promise<boolean>;
}

function EditCommunitySettingDialogComponent({
  isOpen,
  onClose,
  currentName,
  itemType,
  loading,
  errors,
  resetErrors,
  onSave,
}: EditCommunitySettingDialogProps) {
  const [name, setName] = useState(currentName);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form when dialog opens or item changes
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setValidationError(null);
    }
  }, [isOpen, currentName]);

  const isFormValid = () => {
    const trimmed = name.trim();

    return trimmed.length > 0 && trimmed.length <= MAX_NAME_LENGTH && trimmed !== currentName;
  };

  const handleClose = useCallback(() => {
    resetErrors();
    onClose();
  }, [resetErrors, onClose]);

  const handleSave = useCallback(async () => {
    const trimmed = name.trim();

    if (trimmed.length === 0 || trimmed.length > MAX_NAME_LENGTH) {
      setValidationError(`Name must be between 1 and ${MAX_NAME_LENGTH} characters`);

      return;
    }

    // No change — just close
    if (trimmed === currentName) {
      handleClose();

      return;
    }

    setValidationError(null);
    resetErrors();

    const success = await onSave(trimmed);

    if (success) {
      handleClose();
    }
  }, [name, currentName, onSave, handleClose, resetErrors]);

  const titleCasedItemType = toTitleCase(itemType);

  return (
    <Dialog
      cancelAction={{ label: 'Cancel' }}
      isOpen={isOpen}
      primaryAction={{
        label: loading ? (
          <>
            <span aria-hidden="true">
              <LoadingSpinner />
            </span>{' '}
            Save Changes
          </>
        ) : (
          'Save Changes'
        ),
        onClick: handleSave,
        disabled: loading || !isFormValid(),
        variant: 'primary',
      }}
      title={`Edit ${titleCasedItemType} Settings`}
      onClose={handleClose}
    >
      <div className={styles.form}>
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title={`There was an error updating the ${itemType}:`} />}
        <TextInput
          autoFocus
          data-testid="edit-setting-input"
          disabled={loading}
          error={validationError ?? undefined}
          hasError={!!validationError}
          label={`${titleCasedItemType} Name`}
          maxLength={MAX_NAME_LENGTH}
          value={name}
          onChange={setName}
        />
      </div>
    </Dialog>
  );
}

export const EditCommunitySettingDialog = withAppInsightsTracking(EditCommunitySettingDialogComponent, 'EditCommunitySettingDialog');
